'use client';

import { MenuLinkSliceWithSubMenu } from '../../../prismicio-types';
import { SliceZone } from '@prismicio/react';
import { components } from '@/slices';
import { useMenuContext } from '@/context/MenuContext';
import { Content } from '@prismicio/client';

export type PopOverMenuProps = {
  slice: MenuLinkSliceWithSubMenu;
  context:
    | {
        [key: string]: Content.SubmenuSlice[];
      }
    | undefined;
  isHovered: boolean;
  showMenu: () => void;
  hideMenu: () => void;
};

export const PopOverMenu = ({
  slice,
  context,
  isHovered,
  showMenu,
  hideMenu,
}: PopOverMenuProps) => {
  const { isMobile, popOverStates } = useMenuContext();
  const isPopOverMenuOpen = popOverStates[slice.primary.tag!];
  const submenuSlices = context?.[slice.primary.tag!];
  const firstSubmenuSlice = submenuSlices?.[0];

  const isModernSubmenu =
    firstSubmenuSlice &&
    'slice_type' in firstSubmenuSlice &&
    firstSubmenuSlice.slice_type === 'submenu' &&
    'variation' in firstSubmenuSlice &&
    firstSubmenuSlice.variation === 'modernSubmenu';

  const renderSliceZone = () => (
    <SliceZone
      slices={context?.[slice.primary.tag!]}
      components={components}
      context={context}
    />
  );

  if (isMobile) {
    return (
      <div
        className={`${isPopOverMenuOpen ? '' : 'hidden'} bg-white bg-opacity-10 px-5`}
      >
        <div className="mt-6 mb-6 w-full pointer-events-auto">
          <div className="flex flex-col justify-between gap-12">
            {renderSliceZone()}
          </div>
        </div>
      </div>
    );
  }

  const modernMenuContainerClasses =
    'fixed inset-x-0 top-[85px] z-30 flex justify-center transform duration-300 pointer-events-none';
  const modernMenuTransitionClasses = `${
    isHovered
      ? 'visible opacity-100 translate-y-0'
      : 'invisible opacity-0 -translate-y-4'
  } transition-all duration-300`;

  const defaultMenuContainerClasses = `lg:ml-0 w-full fixed inset-0 h-80 transform duration-300`;
  const defaultMenuTransitionClasses = `${
    isHovered
      ? 'visible opacity-100 translate-y-0'
      : 'invisible opacity-0 -translate-y-full'
  } transition-all duration-300`;

  return (
    <div
      className={
        isModernSubmenu
          ? `${modernMenuContainerClasses} ${modernMenuTransitionClasses}`
          : `${defaultMenuContainerClasses} ${defaultMenuTransitionClasses}`
      }
    >
      {isModernSubmenu ? (
        <div
          onMouseEnter={showMenu}
          onMouseLeave={hideMenu}
          className="relative pointer-events-auto"
        >
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] border-b-neutral-800/80" />
          <div className="inline-flex backdrop-blur-xl bg-neutral-800/80 rounded-2xl p-8">
            {renderSliceZone()}
          </div>
        </div>
      ) : (
        <div
          onMouseEnter={showMenu}
          onMouseLeave={hideMenu}
          className="relative bg-menu-gradient text-white w-full h-full px-10 py-6 text-left flex items-center justify-center mt-[75px]"
        >
          <div className="flex flex-col lg:flex-row justify-between 2xl:justify-center gap-16 w-full h-full overflow-hidden">
            {renderSliceZone()}
          </div>
        </div>
      )}
    </div>
  );
};
